import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  Archive,
  Award,
  BarChart3,
  BookOpen,
  Building2,
  Calculator,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Cloud,
  Cog,
  FileStack,
  FileText,
  Gauge,
  GitBranch,
  Handshake,
  Home,
  Info,
  LineChart,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  Lock,
  LogOut,
  Mail,
  Menu,
  MessageSquare,
  PieChart,
  Plug,
  Receipt,
  Scale,
  ScrollText,
  Server,
  Settings as SettingsIcon,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Umbrella,
  UserPlus,
  UsersRound,
  Wrench,
  Workflow,
  X,
} from 'lucide-react';
import { useTranslation } from '@solara/i18n';
import { Button, LanguageToggle, ProviderBadge, ThemeToggle, cn } from '@solara/ui-shared';
import { RoleSwitcher } from '../auth/RoleSwitcher';
import { NotificationsButton } from '../components/NotificationsButton';
import { useAuth, type AdminCapability } from '../auth/AuthContext';
import { useSession } from '../auth/SessionContext';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  cap?: AdminCapability;
}

interface NavSection {
  /** Stable id used as the expand/collapse key — must not change with locale. */
  id: string;
  title: string;
  /** Primary route the section header links to. */
  to: string;
  icon: React.ReactNode;
  items: NavItem[];
}

const COLLAPSE_KEY = 'solara.admin.sidebarCollapsed';

export default function AppShell() {
  const { t } = useTranslation('admin');
  const { t: tP1 } = useTranslation('p1');
  const { t: tLogin } = useTranslation('login');
  const { currentUser, can } = useAuth();
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  function handleSignOut() {
    session.signOut();
    navigate('/login', { replace: true });
  }

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = mobileOpen ? 'hidden' : prev;
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  // Each nav-item gets a unique lucide icon — see icon-uniqueness invariant.
  // Sectioned into 9 functional groups for easier scanning at scale.
  // Each section is also a link (to its primary route) AND toggles its
  // children open/closed via the chevron at the end. Only the section
  // containing the active route is auto-expanded.
  const navSections: NavSection[] = [
    {
      id: 'main',
      title: tP1('navSections.main'),
      to: '/',
      icon: <Home className="h-5 w-5" />,
      items: [
        { to: '/', label: t('nav.dashboard'), icon: <LayoutDashboard className="h-4 w-4" /> },
      ],
    },
    {
      id: 'crm',
      title: tP1('navSections.crm'),
      to: '/tenants',
      icon: <Handshake className="h-5 w-5" />,
      items: [
        { to: '/tenants',           label: t('nav.tenants'),       icon: <Building2 className="h-4 w-4" />, cap: 'tenants.read' },
        { to: '/crm/leads',         label: tP1('nav.crmLeads'),    icon: <UserPlus className="h-4 w-4" /> },
        { to: '/crm/opportunities', label: tP1('nav.crmPipeline'), icon: <Workflow className="h-4 w-4" /> },
        { to: '/crm/reports',       label: tP1('nav.crmReports'),  icon: <BarChart3 className="h-4 w-4" /> },
      ],
    },
    {
      id: 'invoicing',
      title: tP1('navSections.invoicing'),
      to: '/invoices',
      icon: <FileStack className="h-5 w-5" />,
      items: [
        { to: '/invoices', label: t('nav.invoices'), icon: <FileText className="h-4 w-4" />, cap: 'invoices.read.cross_tenant' },
        { to: '/archive',  label: t('nav.archive'),  icon: <Archive className="h-4 w-4" />,  cap: 'invoices.read.cross_tenant' },
      ],
    },
    {
      id: 'accounting',
      title: tP1('navSections.accounting'),
      to: '/accounting/gl-accounts',
      icon: <Calculator className="h-5 w-5" />,
      items: [
        { to: '/accounting/gl-accounts', label: tP1('nav.glAccounts'), icon: <BookOpen className="h-4 w-4" /> },
      ],
    },
    {
      id: 'tax',
      title: tP1('navSections.tax'),
      to: '/tax-review',
      icon: <Scale className="h-5 w-5" />,
      items: [
        { to: '/tax-review', label: tP1('nav.taxReview'), icon: <Receipt className="h-4 w-4" /> },
      ],
    },
    {
      id: 'reports',
      title: tP1('navSections.reports'),
      to: '/reports',
      icon: <PieChart className="h-5 w-5" />,
      items: [
        { to: '/reports',    label: t('nav.reports'),   icon: <LineChart className="h-4 w-4" />, cap: 'reports.read' },
        { to: '/compliance/dashboard', label: tP1('nav.complianceDashboard'), icon: <Gauge className="h-4 w-4" /> },
      ],
    },
    {
      id: 'settings',
      title: tP1('navSections.settings'),
      to: '/settings',
      icon: <Cog className="h-5 w-5" />,
      items: [
        { to: '/rules',                    label: t('nav.rules'),       icon: <SlidersHorizontal className="h-4 w-4" />, cap: 'rules.read' },
        { to: '/workflow/definitions',     label: tP1('nav.workflows'), icon: <GitBranch className="h-4 w-4" /> },
        { to: '/notifications/newsletter', label: tP1('nav.newsletter'),icon: <Mail className="h-4 w-4" /> },
        { to: '/settings',                 label: t('nav.settings'),    icon: <SettingsIcon className="h-4 w-4" /> },
      ],
    },
    {
      id: 'administration',
      title: tP1('navSections.administration'),
      to: '/kyc-review',
      icon: <Shield className="h-5 w-5" />,
      items: [
        { to: '/kyc-review',         label: tP1('nav.kycReview'),       icon: <ShieldCheck className="h-4 w-4" /> },
        { to: '/security/roles',     label: tP1('nav.customRoles'),     icon: <UsersRound className="h-4 w-4" /> },
        { to: '/security/idp',       label: tP1('nav.idpFederation'),   icon: <KeyRound className="h-4 w-4" /> },
        { to: '/compliance/insurances', label: tP1('nav.insurances'),   icon: <Umbrella className="h-4 w-4" /> },
        { to: '/compliance/iso',     label: tP1('nav.isoCerts'),        icon: <Award className="h-4 w-4" /> },
        { to: '/security/incidents', label: tP1('nav.securityIncidents'), icon: <AlertTriangle className="h-4 w-4" /> },
        { to: '/tasks',              label: tP1('nav.taskInbox'),       icon: <ListChecks className="h-4 w-4" /> },
        { to: '/chat',               label: tP1('nav.chat'),            icon: <MessageSquare className="h-4 w-4" /> },
      ],
    },
    {
      id: 'operations',
      title: tP1('navSections.operations'),
      to: '/monitoring',
      icon: <Server className="h-5 w-5" />,
      items: [
        { to: '/monitoring',       label: t('nav.monitoring'),        icon: <Activity className="h-4 w-4" />,   cap: 'monitoring.read' },
        { to: '/tickstar',         label: t('nav.tickstar'),          icon: <Cloud className="h-4 w-4" />,      cap: 'tickstar.read' },
        { to: '/integrations/erp', label: tP1('nav.erpConnectors'),   icon: <Plug className="h-4 w-4" /> },
        { to: '/certificates',     label: t('nav.certificates'),      icon: <Lock className="h-4 w-4" />,       cap: 'certificates.read' },
        { to: '/audit',            label: t('nav.audit'),             icon: <ScrollText className="h-4 w-4" />, cap: 'audit.read' },
        { to: '/tools',            label: t('nav.tools'),             icon: <Wrench className="h-4 w-4" />,     cap: 'tickstar.read' },
        { to: '/about',            label: t('nav.about'),             icon: <Info className="h-4 w-4" /> },
      ],
    },
  ];

  // Filter capability-gated items, then drop empty sections.
  const visibleSections = useMemo(
    () =>
      navSections
        .map((s) => ({ ...s, items: s.items.filter((it) => !it.cap || can(it.cap)) }))
        .filter((s) => s.items.length > 0),
    [navSections, can],
  );

  // Auto-expand the section that contains the active route. Subsequent
  // manual toggles are tracked in `expanded`.
  const activeSectionId = useMemo(() => {
    for (const s of visibleSections) {
      if (s.items.some((it) => isRouteActive(location.pathname, it.to))) return s.id;
    }
    return visibleSections[0]?.id ?? null;
  }, [visibleSections, location.pathname]);

  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(activeSectionId ? [activeSectionId] : []),
  );

  useEffect(() => {
    if (!activeSectionId) return;
    setExpanded((prev) => {
      if (prev.has(activeSectionId)) return prev;
      const next = new Set(prev);
      next.add(activeSectionId);
      return next;
    });
  }, [activeSectionId]);

  function toggleSection(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const sidebarBody = (variant: 'desktop' | 'mobile') => {
    const isCollapsed = variant === 'desktop' && collapsed;
    return (
      <>
        <div className={cn(
          'flex items-center gap-3 h-16 border-b border-border',
          isCollapsed ? 'px-3 justify-center' : 'px-6',
        )}>
          <div className="h-8 w-8 rounded-md bg-brand-600 grid place-items-center text-white font-semibold shrink-0">A</div>
          {!isCollapsed ? (
            <div className="flex flex-col leading-tight min-w-0">
              <span className="text-sm font-semibold truncate">Solara Platform</span>
              <span className="text-xs text-fg-subtle truncate">Admin Console</span>
            </div>
          ) : null}
          {variant === 'mobile' ? (
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              aria-label={t('nav.collapse')}
              className="ms-auto h-9 w-9 grid place-items-center rounded-md text-fg-muted hover:bg-surface-muted"
            >
              <X className="h-5 w-5" />
            </button>
          ) : null}
        </div>
        <nav className={cn(
          'flex-1 text-sm overflow-y-auto',
          isCollapsed ? 'p-2 space-y-0.5' : 'p-3 space-y-1',
        )}>
          {visibleSections.map((section) => {
            if (isCollapsed) {
              // Collapsed (icon-only) mode falls back to a flat icon list —
              // section titles can't be shown.
              return (
                <div key={section.id} className="space-y-0.5">
                  {section.items.map((it) => (
                    <SideLink key={it.to} {...it} collapsed />
                  ))}
                </div>
              );
            }
            const isOpen = expanded.has(section.id);
            const hasActiveChild = section.items.some((it) =>
              isRouteActive(location.pathname, it.to),
            );
            return (
              <div key={section.id}>
                <SectionHeader
                  section={section}
                  isOpen={isOpen}
                  isActive={hasActiveChild}
                  onToggle={() => toggleSection(section.id)}
                />
                {isOpen && (
                  <div className="mt-0.5 mb-1 ms-3 ps-3 border-s border-border space-y-0.5">
                    {section.items.map((it) => (
                      <SideLink key={it.to} {...it} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className={cn(
          'border-t border-border text-xs text-fg-subtle',
          isCollapsed ? 'p-2 space-y-2' : 'p-4 space-y-3',
        )}>
          {!isCollapsed ? (
            <>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-success" />
                  <span>FTA-accredited ASP</span>
                </div>
                <div className="font-medium text-fg truncate">{currentUser.givenName} {currentUser.familyName}</div>
                <div className="truncate">{currentUser.email}</div>
                <div className="text-[10px] truncate">{currentUser.team}</div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignOut}
                className="w-full gap-2"
              >
                <LogOut className="h-3.5 w-3.5 rtl-mirror" />
                {tLogin('session.signOut')}
              </Button>
              <ProviderBadge
                variant="live"
                label={t('tickstar:brand.partnerLong')}
                sub={t('tickstar:brand.providerRole')}
                className="w-full"
              />
            </>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div title={`${currentUser.givenName} ${currentUser.familyName}`}>
                <ShieldCheck className="h-4 w-4 text-success" />
              </div>
              <button
                type="button"
                onClick={handleSignOut}
                aria-label={tLogin('session.signOut')}
                title={tLogin('session.signOut')}
                className="h-9 w-9 grid place-items-center rounded-md text-fg-muted hover:bg-surface-muted hover:text-danger"
              >
                <LogOut className="h-4 w-4 rtl-mirror" />
              </button>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div
      className={cn(
        'min-h-full grid grid-cols-1 bg-bg',
        collapsed ? 'lg:grid-cols-[72px_1fr]' : 'lg:grid-cols-[260px_1fr]',
      )}
    >
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col border-e border-border bg-surface sticky top-0 h-screen">
        {sidebarBody('desktop')}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
          title={collapsed ? t('nav.expand') : t('nav.collapse')}
          className="h-9 mx-2 mb-2 rounded-md text-fg-muted hover:bg-surface-muted flex items-center justify-center gap-2"
        >
          {collapsed ? (
            <ChevronsRight className="h-4 w-4 rtl-mirror" />
          ) : (
            <>
              <ChevronsLeft className="h-4 w-4 rtl-mirror" />
              <span className="text-xs">{t('nav.collapse')}</span>
            </>
          )}
        </button>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen ? (
        <>
          <button
            type="button"
            aria-label={t('nav.collapse')}
            onClick={() => setMobileOpen(false)}
            className="lg:hidden fixed inset-0 z-30 bg-black/40 backdrop-blur-sm"
          />
          <aside
            role="dialog"
            aria-modal="true"
            className={cn(
              'lg:hidden fixed inset-y-0 start-0 z-40 w-[84vw] max-w-[320px]',
              'flex flex-col border-e border-border bg-surface shadow-xl',
              'animate-in slide-in-from-start duration-200',
            )}
          >
            {sidebarBody('mobile')}
          </aside>
        </>
      ) : null}

      <div className="flex flex-col min-w-0">
        <header className="sticky top-0 z-10 h-16 border-b border-border bg-surface/80 backdrop-blur supports-[backdrop-filter]:bg-surface/70 flex items-center gap-2 px-3 sm:px-4 lg:px-8">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label={t('nav.openMenu')}
            className="lg:hidden h-9 w-9 grid place-items-center rounded-md text-fg-muted hover:bg-surface-muted"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="lg:hidden h-8 w-8 rounded-md bg-brand-600 grid place-items-center text-white font-semibold">A</div>
          <Breadcrumb t={t} />
          <div className="ms-auto flex items-center gap-1">
            <LiveIndicator t={t} />
            <NotificationsButton />
            <RoleSwitcher />
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SectionHeader({
  section,
  isOpen,
  isActive,
  onToggle,
}: {
  section: NavSection;
  isOpen: boolean;
  isActive: boolean;
  onToggle: () => void;
}) {
  return (
    <NavLink
      to={section.to}
      onClick={() => {
        if (!isOpen) onToggle();
      }}
      end={section.to === '/'}
      className={({ isActive: navActive }) =>
        cn(
          'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
          isActive || navActive
            ? 'bg-brand-50 text-brand-800 font-semibold'
            : 'text-fg hover:bg-surface-muted',
        )
      }
    >
      {section.icon}
      <span className="flex-1 truncate text-start">{section.title}</span>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle();
        }}
        aria-label={isOpen ? 'collapse' : 'expand'}
        className="rounded p-0.5 text-fg-muted hover:bg-surface"
      >
        {isOpen ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 rtl-mirror" />
        )}
      </button>
    </NavLink>
  );
}

function SideLink({
  to,
  icon,
  label,
  collapsed,
}: NavItem & { collapsed?: boolean }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-md transition-colors duration-150',
          collapsed ? 'h-10 w-10 mx-auto justify-center' : 'gap-2.5 px-3 py-1.5 text-[13px]',
          isActive
            ? 'bg-brand-50 text-brand-800 font-medium'
            : 'text-fg-muted hover:bg-surface-muted hover:text-fg',
        )
      }
    >
      {icon}
      {!collapsed ? <span className="truncate">{label}</span> : null}
    </NavLink>
  );
}

function isRouteActive(currentPath: string, to: string): boolean {
  if (to === '/') return currentPath === '/';
  return currentPath === to || currentPath.startsWith(`${to}/`);
}

function Breadcrumb({ t }: { t: (k: string) => string }) {
  const location = useLocation();
  const path = location.pathname;
  const label =
    path.startsWith('/tenants') ? t('nav.tenants') :
    path.startsWith('/invoices') ? t('nav.invoices') :
    path.startsWith('/archive') ? t('nav.archive') :
    path.startsWith('/rules') ? t('nav.rules') :
    path.startsWith('/audit') ? t('nav.audit') :
    path.startsWith('/monitoring') ? t('nav.monitoring') :
    path.startsWith('/tickstar') ? t('nav.tickstar') :
    path.startsWith('/certificates') ? t('nav.certificates') :
    path.startsWith('/reports') ? t('nav.reports') :
    path.startsWith('/tools') ? t('nav.tools') :
    path.startsWith('/about') ? t('nav.about') :
    path.startsWith('/settings') ? t('nav.settings') :
    t('nav.dashboard');
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-fg-muted min-w-0">
      <span className="font-medium text-fg truncate">Solara Admin</span>
      <ChevronRight className="h-4 w-4 rtl-mirror shrink-0" />
      <span className="truncate">{label}</span>
    </nav>
  );
}

function LiveIndicator({ t }: { t: (k: string) => string }) {
  return (
    <span
      className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-success/10 text-success px-2 py-1 text-[11px] font-medium"
      title={t('live.tooltip')}
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-success/50 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
      </span>
      {t('live.label')}
    </span>
  );
}
